import * as React from "react"

import { cn } from "#app/utils/misc.tsx"

const Card = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn(
			"rounded-lg border bg-card text-card-foreground shadow-sm",
			className,
		)}
		{...props}
	/>
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & {
		actions?: React.ReactNode
		hiddenActions?: React.ReactNode
	}
>(({ className, actions, hiddenActions, children, ...props }, ref) => (
	<div ref={ref} className="p-6 pb-4" {...props}>
		<div className="flex items-center justify-between">
			<div className={cn("flex flex-col space-y-1.5 ", className)}>
				{children}
			</div>
			<div className="flex items-center gap-2">
				{actions && actions}
				{hiddenActions && hiddenActions}
			</div>
		</div>
	</div>
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h3
		ref={ref}
		className={cn(
			"text-xl font-semibold leading-none tracking-tight",
			className,
		)}
		{...props}
	/>
))
CardTitle.displayName = "CardTitle"

const CardHiddenActions = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("font-semibold leading-none tracking-tight", className)}
		{...props}
	/>
))
CardHiddenActions.displayName = "CardHiddenActions"

const CardActions = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("flex items-center gap-2", className)}
		{...props}
	/>
))
CardActions.displayName = "CardActions"

const CardDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<p
		ref={ref}
		className={cn("text-sm text-muted-foreground", className)}
		{...props}
	/>
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("flex items-center p-6 pt-0", className)}
		{...props}
	/>
))
CardFooter.displayName = "CardFooter"

export {
	Card,
	CardHeader,
	CardActions,
	CardHiddenActions,
	CardFooter,
	CardTitle,
	CardDescription,
	CardContent,
}
